export const CANVAS_RULES_PROMPT = `Você deve gerar um HTML5 completo e responsivo (sem markdown e sem blocos de código).
Use Tailwind CSS e Chart.js com as mesmas bibliotecas dos exemplos.

Paleta obrigatória para montar o HTML:
- Tech Graphite (primary): #2C3E50
- Strategic Dark Blue: #1B3A57
- Deep Tech Blue: #000E28
- Focus Orange (destaques): #FF6B35
- Clean Light (background): #F5F7FA

Entrada recebida:
- context_block (User Message / History / Summary / RAG Context)
- task_details (description/expected_output/agent/backstory)
- base_result (resposta do crew original)

MODO CANVAS ATIVADO - GERAÇÃO DE APLICAÇÃO FUNCIONAL HTML5
- A resposta DEVE ser APENAS HTML5, SEM blocos de código markdown.
- O HTML deve ser um documento completo e válido, pronto para renderização em iframe.
- Não gere apenas um relatório estático: gere uma aplicação funcional com interações reais.
- Toda interação do usuário deve ter ação concreta (local ou integrada ao backend).
- Sempre gere HTML legível para usuário não técnico, focado em informação e usabilidade.

CONTRATO OBRIGATÓRIO DA FERRAMENTA (NÃO OMITIR):
NOTA: Existem dois modos de canvas:
  A) Aplicação CRUD/gestão → itens 1–7 são todos obrigatórios.
  B) Relatório/análise de dados (somente leitura) → item 8 é obrigatório; itens 1, 3, 4, 4.1, 5 são OPCIONAIS.
     Apenas \`__devspottGetState\`/\`__devspottSetState\` (item 2) e botões com ação (item 6) continuam obrigatórios.

1) Campos dinâmicos/editáveis (obrigatório em modo A, opcional em modo B):
- Quando houver campos editáveis, usar obrigatoriamente atributo \`data-field\`.
- Não usar placeholders no formato \`{{campo}}\`.
- Exemplos válidos: \`<input data-field="nome_cliente">\`, \`<textarea data-field="resumo">\`.

2) Estado persistente da aplicação:
- Use OBRIGATORIAMENTE \`window.__devspottStore\` para gerenciar o estado reativo.
- O store é instanciado em \`window.__devspottStore\` e possui os métodos: \`get(path)\`, \`set(path, value)\`, \`watch(path, cb)\`, \`push(path, item)\`, \`replaceById(path, id, item)\`, \`removeById(path, id)\`, \`derive(path, deps, computeFn)\`, \`snapshot()\`, \`hydrate(snap)\`.
- Implementar obrigatoriamente:
  - \`window.__devspottGetState = function () { return window.__devspottStore.snapshot(); }\`
  - \`window.__devspottSetState = function (state) { window.__devspottStore.hydrate(state); }\`
- Inclua no state: \`__devspottContractVersion: 2\`.

3) Runtime action bridge:
- A função \`window.__devspottRuntimeAction(action, params)\` é FORNECIDA PELA PLATAFORMA e injetada automaticamente.
- NÃO reimplemente esta função. NÃO defina \`window.__devspottRuntimeAction = function ...\`. Apenas CHAME-a.
- A função envia a ação ao backend e retorna um \`request_id\`. O resultado chega via \`message\` event (ver item 4).
- Ações disponíveis: \`record.list\`, \`record.get\`, \`record.create\`, \`record.update\`, \`record.delete\`.
- O campo de dados é \`data\` (NÃO use \`payload\`).
- Exemplo create:
  \`window.__devspottRuntimeAction('record.create', { entity_type: 'tasks', data: { title: 'Nova tarefa', status: 'todo' } })\`
- Exemplo list:
  \`window.__devspottRuntimeAction('record.list', { entity_type: 'tasks' })\`
- Exemplo get por ID (detalhe de um item específico):
  \`window.__devspottRuntimeAction('record.get', { entity_type: 'tasks', record_id: 'uuid-do-record' })\`
  Também aceita \`external_id\` como identificador alternativo ao \`record_id\`.
  Resultado chega via \`message\` event com \`event.data.runtime_action === 'record.get'\` e \`event.data.result\` contendo o record completo.
  Use \`record.get\` para: carregar detalhe ao abrir modal/tela de detalhe, pré-popular formulário de edição sem ter o item na memória, atualizar um item específico sem recarregar toda a lista.
  NÃO use \`record.get\` quando o item já estiver disponível no estado local (ex: ao clicar em item de lista já carregada).
- Exemplo update (identidade obrigatória):
  \`window.__devspottRuntimeAction('record.update', { entity_type: 'tasks', record_id: 'task_1', data: { status: 'done' } })\`
- Exemplo delete (identidade obrigatória):
  \`window.__devspottRuntimeAction('record.delete', { entity_type: 'tasks', record_id: 'task_1' })\`

4) Listener de resultado assíncrono:
- Adicionar \`window.addEventListener('message', ...)\` para tratar \`runtime_action_result\`.
- O resultado vem como \`event.data\` com \`{ type: 'runtime_action_result', status: 'success'|'error', result: {...}, request_id: '...' }\`.
- Em sucesso, roteie o fluxo por \`event.data.runtime_action\` (ou \`event.data.action\`) em vez de inferir só por shape de \`result\`.
- Para \`record.list\`, tratar ambos os formatos: \`event.data.result\` como array OU \`event.data.result.items\` como array.
- Em erro, usar \`event.data.message\` com fallback em \`event.data.error\`.
- NÃO chamar \`record.list\` no handler quando \`event.data.runtime_action === 'record.list'\`.

4.1) Reidratação e persistência obrigatórias para app funcional:
- Ao carregar a aplicação (\`DOMContentLoaded\`), chamar \`record.list\` para cada entidade usada e renderizar a UI a partir dos dados retornados.
- Após \`record.list\` retornar com itens: se nenhum registro estiver selecionado (\`currentRecordId\` nulo), OBRIGATORIAMENTE carregar e exibir o primeiro item da lista automaticamente. NÃO deixar a tela principal em branco quando há registros disponíveis.
- Ações de criar/editar/excluir DEVEM refletir em persistência backend (via \`record.create\`, \`record.update\`, \`record.delete\`), não apenas em estado local.
- Botões com rótulos como "Salvar", "Criar", "Atualizar", "Excluir", "Concluir", "Mover" NÃO podem ser apenas visuais; devem disparar ação concreta de runtime.
- Após \`record.create\`/\`record.update\`/\`record.delete\`, atualizar a tela com \`runtime_action_result\` e/ou recarregar via \`record.list\`.
- Não invente comportamento "mock": se houver CRUD de entidade, a fonte de verdade é backend runtime.
- NUNCA implemente loop de listagem: não chamar \`record.list\` dentro do handler de \`runtime_action_result\` quando o próprio \`runtime_action\` já é \`record.list\`.
- Evite decisões ambíguas por \`Array.isArray(result)\` como regra principal; prefira \`runtime_action\` para decidir o fluxo.
- Para ações de persistência, o payload \`data\` deve incluir também campos calculados/derivados (não apenas valores de \`input\`).
- Preferir montar \`data\` a partir do estado completo da aplicação (\`__devspottGetState\`) e então sobrescrever com edições atuais do formulário.
- Estrutura do objeto record entregue pelo runtime: a plataforma faz spread dos campos de \`data\` no nível raiz do objeto, E mantém \`data\` aninhado. Portanto \`record.title\`, \`record.status\` etc. estão disponíveis diretamente. Use SEMPRE o nível raiz (\`record[field]\`) para leitura de campos. NÃO misture \`record[field]\` com \`record.data[field]\` no mesmo componente.
- Para popular formulários de edição, itere sobre \`[data-field]\` e leia do nível raiz: \`document.querySelectorAll("[data-field]").forEach(el => { const v = record[el.getAttribute("data-field")]; el.value = v !== undefined && v !== null ? v : ""; });\`

4.2) Estilo padrão de campos de formulário (modo A):
- Use sempre o mesmo padrão visual: \`<div class="field">\` + \`<label class="input__label" for="...">\` + controle + (opcional) \`<p class="input__hint">\` + (erro) \`<p class="input__error-message" role="alert">\`.
- \`<input>\` (text, email, number, date, etc.) e \`<textarea>\`: classe \`input\`; \`<select>\`: classes \`input select\`; checkbox/radio: \`<label class="input-choice">\` (ou \`input-choice--inline\`) envolvendo o input e o texto.
- Estados: foco visível (\`:focus-visible\`); \`:disabled\` com opacidade reduzida; erro com \`input--error\` no controle e mensagem em \`input__error-message\`.
- Altura tocável mínima (~2.5rem) em inputs de uma linha. Inclua no \`<style>\` regras base para \`.field\`, \`.input__label\`, \`.input__hint\`, \`.input-choice\`.

4.3) Lista, detalhe, editar e excluir (modo A com persistência de registros):
- Para cada entidade com \`record.create\` / \`record.update\` / \`record.delete\`, a UI DEVE ter: (1) **view Lista** — lista/tabela/cartões com \`record.list\` no carregamento e após mutações; linha ou cartão clicável; botão **Novo/Adicionar** com \`record.create\`; (2) **view Detalhe** — um registro por vez (\`record.get\` ao entrar ou dados do item selecionado), campos visíveis; (3) na detalhe, botão **Voltar** (\`type="button"\`) no topo ou na toolbar que volta à **view Lista** (troca de view/estado; não usar o mesmo rótulo de Excluir); (4) botões **Editar** (formulário + \`record.update\`) e **Excluir** (\`record.delete\` com confirmação em modal).
- Navegação clara entre Lista e Detalhe usando roteamento declarativo: \`window.__devspottRouter.go('rota', params)\`. Após \`record.create\` com sucesso, navegar para a lista ou para o detalhe do registro criado.

5) Domínio de entidades (obrigatório quando houver CRUD):
- Incluir script JSON com id \`devspott-runtime-domain\`:
  \`<script id="devspott-runtime-domain" type="application/json">...</script>\`
- \`entity_type\` usado nas ações deve existir nesse domínio.
- Estrutura mínima por entidade:
  \`{ "name": "tasks", "required_fields": [], "optional_fields": [], "payload_schema": { "required": [], "properties": {} } }\`

6) Botões e interações:
- TODO botão (\`<button>\`) DEVE ter o atributo \`type="button"\` explicitamente. Nunca omitir type.
- TODO botão deve ter uma ação: \`onclick\`, \`addEventListener('click')\`, ou \`data-runtime-action\`.
- NUNCA gerar botão decorativo/inerte sem ação real.
- NUNCA usar \`<form>\` com action de URL que cause navegação. Se usar \`<form>\`, SEMPRE interceptar submit: \`form.addEventListener('submit', e => e.preventDefault())\`.
- Links de ação (email/download/open_link) devem funcionar.

7) Drag and drop (se houver):
- Elementos arrastáveis devem incluir \`data-entity-type\` e \`data-record-id\` ou \`data-external-id\`.

8) Relatório de dados / análise de grande volume (OBRIGATÓRIO quando base_result contém dados tabulares ou JSON do Code Interpreter):
- O Code Interpreter pode analisar milhares de registros e gerar saída JSON estruturada.
- Para precisão, NÃO inventar, estimar ou extrapolar números no HTML: toda métrica exibida deve vir diretamente dos campos \`metrics\`/\`quality_checks\` do JSON retornado.
- Exibir no topo um bloco de qualidade com: \`total_rows\`, \`rows_processed\`, e alertas de colunas com nulos quando \`quality_checks.null_counts\` indicar valores > 0.
- Exibir sempre um aviso visível para o usuário: "A visualização na tela é resumida. Para acessar a análise completa, baixe o arquivo."
- Quando base_result contiver dados tabulares (array de objetos, CSV parseado, resultado de análise), embutir o dataset como JSON em:
  \`<script id="devspott-analysis-dataset" type="application/json">{ "columns": [...], "data": [...], "metrics": {...}, "total_rows": N }</script>\`
- Renderizar uma tabela HTML paginada client-side (sem biblioteca externa):
  - Controles prev/next com indicação "Página X de Y".
  - Seletor de itens por página (10, 25, 50, 100).
  - Busca/filtro rápido por texto acima da tabela.
  - Ordenação por coluna ao clicar no header (toggle asc/desc com indicador visual ▲/▼).
- Mostrar métricas/KPIs em cards ACIMA da tabela sempre que houver dados numéricos.
- Se houver agregações relevantes (totais por categoria, médias, tendências), incluir gráficos Chart.js.
- Incluir botões de download via bridge de runtime action:
  \`\`\`
  function downloadXLSX() {
    var ds = JSON.parse(document.getElementById('devspott-analysis-dataset').textContent || '{}');
    if (typeof window.__devspottRuntimeAction === 'function') {
      var reqId = window.__devspottRuntimeAction('tool.generate_artifact', {
        format: 'xlsx',
        dataset_ref: 'devspott-analysis-dataset',
        filename: 'relatorio-completo.xlsx',
        records: ds.data,
        columns: ds.columns
      });
      if (reqId && window.__devspottStore && window.__devspottStore.push) {
        window.__devspottStore.push('runtime.activeJobs', reqId);
      }
    }
  }

  function downloadPDF() {
    var ds = JSON.parse(document.getElementById('devspott-analysis-dataset').textContent || '{}');
    if (typeof window.__devspottRuntimeAction === 'function') {
      var reqId = window.__devspottRuntimeAction('tool.generate_artifact', {
        format: 'pdf',
        dataset_ref: 'devspott-analysis-dataset',
        filename: 'relatorio-completo.pdf',
        records: ds.data,
        columns: ds.columns
      });
      if (reqId && window.__devspottStore && window.__devspottStore.push) {
        window.__devspottStore.push('runtime.activeJobs', reqId);
      }
    }
  }

  function downloadDOCX() {
    var ds = JSON.parse(document.getElementById('devspott-analysis-dataset').textContent || '{}');
    if (typeof window.__devspottRuntimeAction === 'function') {
      var reqId = window.__devspottRuntimeAction('tool.generate_artifact', {
        format: 'docx',
        dataset_ref: 'devspott-analysis-dataset',
        filename: 'relatorio-completo.docx',
        records: ds.data,
        columns: ds.columns
      });
      if (reqId && window.__devspottStore && window.__devspottStore.push) {
        window.__devspottStore.push('runtime.activeJobs', reqId);
      }
    }
  }

  function downloadPPTX() {
    var ds = JSON.parse(document.getElementById('devspott-analysis-dataset').textContent || '{}');
    if (typeof window.__devspottRuntimeAction === 'function') {
      var reqId = window.__devspottRuntimeAction('tool.generate_artifact', {
        format: 'pptx',
        dataset_ref: 'devspott-analysis-dataset',
        filename: 'relatorio-completo.pptx',
        records: ds.data,
        columns: ds.columns
      });
      if (reqId && window.__devspottStore && window.__devspottStore.push) {
        window.__devspottStore.push('runtime.activeJobs', reqId);
      }
    }
  }
  \`\`\`
- Mostrar quatro botões: **Baixar XLSX completo**, **Baixar PDF completo**, **Baixar DOCX completo** e **Baixar PPTX completo**.
- Se \`pdf_base64\` e \`pdf_download_url\` não existirem, renderizar botão de PDF desabilitado + mensagem "PDF completo indisponível para este relatório".
- Para relatórios (somente leitura), NÃO é obrigatório incluir \`data-field\`, \`record.create/update/delete\`, nem \`devspott-runtime-domain\`.
- O canvas de relatório ainda DEVE incluir \`__devspottGetState\` / \`__devspottSetState\` para persistência de estado de filtros, página atual e ordenação.
- Quando o dataset tiver mais de 1000 linhas, o Code Interpreter deve incluir apenas as primeiras 1000 no campo \`data\` e indicar \`"truncated": true\` junto com \`total_rows\` real. Nesse caso, \`xlsx_base64\`/\`xlsx_download_url\`, \`pdf_base64\`/\`pdf_download_url\`, \`docx_base64\`/\`docx_download_url\` e \`pptx_base64\`/\`pptx_download_url\` devem estar disponíveis para download completo.
- Botão de download DEVE verificar truncamento (\`truncated === true\` ou \`total_rows > data.length\`). Se truncado e sem \`xlsx_base64\`/ \`xlsx_download_url\`, exibir erro explícito e NÃO gerar planilha local a partir de \`data\`.
- Botão de PDF DEVE verificar se existe \`pdf_base64\` ou \`pdf_download_url\` no dataset e, se existir, enviar esse campo para \`window.__devspottRequestDownload\`.
- NUNCA depender de \`Blob + URL.createObjectURL + a.click()\` dentro do iframe. Use a bridge de download para o parent.

UI/UX OBRIGATÓRIO (aplicar em modo A e modo B):
- Mostrar estado de loading com skeleton (\`animate-pulse\`) enquanto dados iniciais não carregam; nunca deixar tela em branco.
- Implementar estado vazio com ícone, mensagem clara e botão de ação principal.
- Em ações assíncronas (salvar/criar/atualizar/excluir), botão deve indicar loading (spinner/texto), ficar \`disabled\` e usar \`cursor-not-allowed\`.
- Usar layout responsivo mobile-first com grids adaptativos (\`grid-cols-1 sm:grid-cols-2 lg:grid-cols-3\`) para cards e seções.
- Tabelas devem ficar em \`overflow-x-auto\` com \`min-w-max\` para evitar quebra em telas pequenas.
- Se houver sidebar/menu lateral, implementar versão mobile com overlay e ação de abrir/fechar.
- Aplicar microinterações leves (\`transition-all duration-200\`) em botões, cards e linhas interativas.
- Garantir acessibilidade mínima: \`aria-label\` em botões de ícone, \`role="alert"\` para erros e \`aria-busy\` durante loading.

CORES ESPECÍFICAS PARA HEADER (OBRIGATÓRIO):
- Header Background (escolher uma): #2C3E50, #FF6B35, #1B3A57, #000E28
- Header Text: #F5F7FA

ESTRUTURA CSS OBRIGATÓRIA (inclua no <style>):
1. Chart Container:
.chart-container {
    position: relative;
    width: 100%;
    height: 350px;
    max-height: 600px;
}
@media (min-width: 768px) {
    .chart-container { height: 400px; max-height: 600px; }
}
2. Card Styling:
.card {
    background-color: [surface-color];
    border-radius: 1rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    border: 1px solid [border-color];
    transition: transform 0.2s ease;
}
.card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}
3. Fade In Animation:
@keyframes fadeIn {
    0% { opacity: 0; transform: translateY(10px); }
    100% { opacity: 1; transform: translateY(0); }
}
.animate-fade-in { animation: fadeIn 0.5s ease-out; }

ESTRUTURA DO HTML (referência):
<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>[Título da Aplicação]</title>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Kantumruy+Pro:wght@300;400;500;600;700&display=swap" rel="stylesheet">
    <script src="https://cdn.tailwindcss.com"></script>
    <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
</head>
<body class="[bg-color] [text-color]">
    <header class="[sticky] [bg-gradient] [shadow] [padding]"></header>
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12"></main>
</body>
</html>

VALIDAÇÃO DE GRÁFICOS (CRÍTICO):
- Validar dados (sem arrays vazios, NaN, Infinity, undefined). Quando um dado não existir, usar null.
- Labels e datasets com tamanhos iguais.
- Usar try-catch em TODOS os gráficos.
- Validar existência do canvas antes de criar o gráfico.

IMPORTANTE:
- NUNCA gere dados fictícios.
- Retorne APENAS HTML/CSS/JavaScript puro.
- Comece diretamente com <!DOCTYPE html>.
- Responsividade obrigatória (mobile e desktop).
- Use IDs semânticos e comentários HTML para organização.
- Para aplicações de CRUD/gestão (modo A), integração backend via runtime é obrigatória.
- Para relatórios/análise de dados (modo B), embutir dados em \`devspott-analysis-dataset\`, mostrar aviso de visualização resumida e implementar downloads completos em XLSX e PDF.
- Respeite integralmente o contrato de runtime acima, incluindo persistência e reidratação.
- Quando base_result contiver saída JSON do Code Interpreter com campos como "columns", "data", "metrics", usar modo B automaticamente.`;

export const CANVAS_RULES_SEPARATOR =
  '\n\n---\nREGRAS OBRIGATÓRIAS PARA RESPOSTA AVANÇADA (CANVAS)\n---\n\n';

export const appendCanvasRulesToUserMessage = (userMessage: string): string => {
  return `${userMessage}${CANVAS_RULES_SEPARATOR}${CANVAS_RULES_PROMPT}`;
};

const normalizeCanvasRulesText = (value: string): string => {
  return String(value || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const CANVAS_RULES_SIGNATURE_MARKERS = [
  'MODO CANVAS ATIVADO - GERAÇÃO DE APLICAÇÃO FUNCIONAL HTML5',
  '__devspottGetState',
  '__devspottSetState',
  'devspott-runtime-domain',
];

const hasCanvasRulesSignature = (value: string): boolean => {
  const normalized = normalizeCanvasRulesText(value).toLowerCase();
  if (!normalized) {
    return false;
  }
  let matches = 0;
  CANVAS_RULES_SIGNATURE_MARKERS.forEach(marker => {
    if (normalized.includes(marker.toLowerCase())) {
      matches += 1;
    }
  });
  return matches >= 3;
};

export const stripCanvasRulesFromUserMessage = (
  userMessage: string
): string => {
  if (!userMessage) {
    return '';
  }

  const separatorIndex = userMessage.indexOf(CANVAS_RULES_SEPARATOR);
  if (separatorIndex < 0) {
    return userMessage;
  }

  const baseMessage = userMessage.slice(0, separatorIndex);
  const possibleRulesBlock = userMessage
    .slice(separatorIndex + CANVAS_RULES_SEPARATOR.length)
    .trim();
  if (!possibleRulesBlock) {
    return baseMessage;
  }

  const normalizedPrompt = normalizeCanvasRulesText(CANVAS_RULES_PROMPT);
  const normalizedPossibleRulesBlock =
    normalizeCanvasRulesText(possibleRulesBlock);
  if (
    normalizedPossibleRulesBlock === normalizedPrompt ||
    normalizedPossibleRulesBlock.startsWith(normalizedPrompt) ||
    hasCanvasRulesSignature(possibleRulesBlock)
  ) {
    return baseMessage;
  }

  return userMessage;
};

export const sanitizeUserMessageForDisplay = (userMessage: string): string => {
  return stripCanvasRulesFromUserMessage(userMessage);
};

export interface CanvasDynamicBackendValidationResult {
  isValid: boolean;
  missing: string[];
}

const BUTTON_TAG_REGEX = /<button\b[^>]*>/gi;
const FORM_BLOCK_REGEX = /<form\b[\s\S]*?<\/form>/gi;
const RUNTIME_DOMAIN_REGEX =
  /<script[^>]*id=["']devspott-runtime-domain["'][^>]*>([\s\S]*?)<\/script>/i;
const ENTITY_TYPE_REGEX = /entity_type\s*[:=]\s*["']([a-zA-Z0-9_-]+)["']/g;

const hasContractPrimitive = (html: string, marker: string): boolean =>
  html.includes(marker);

const getTagAttribute = (tag: string, attribute: string): string | null => {
  const regex = new RegExp(`${attribute}\\s*=\\s*["']([^"']+)["']`, 'i');
  const match = tag.match(regex);
  return match?.[1] ?? null;
};

const detectButtonsWithoutAction = (html: string): boolean => {
  const tags = html.match(BUTTON_TAG_REGEX) ?? [];
  if (!tags.length) {
    return false;
  }

  const forms = html.match(FORM_BLOCK_REGEX) ?? [];
  const formBlocksJoined = forms.join('\n');

  return tags.some(tag => {
    if (/onclick\s*=/i.test(tag)) return false;
    if (/data-runtime-action\s*=/i.test(tag)) return false;
    if (/data-devspott-repair-action\s*=/i.test(tag)) return false;

    const type = getTagAttribute(tag, 'type');
    if (type?.toLowerCase() === 'submit' && formBlocksJoined.includes(tag)) {
      return false;
    }

    const buttonId = getTagAttribute(tag, 'id');
    if (buttonId) {
      const escapedId = buttonId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const listenerRegex = new RegExp(
        `getElementById\\(\\s*["']${escapedId}["']\\s*\\)\\s*\\.\\s*addEventListener\\(\\s*["']click["']`,
        'i'
      );
      if (listenerRegex.test(html)) return false;
    }

    return true;
  });
};

const hasRecordUpdateWithoutIdentity = (html: string): boolean => {
  const calls = html.match(
    /__devspottRuntimeAction\s*\(\s*["']record\.update["']\s*,\s*\{[\s\S]*?\}\s*\)/g
  );
  if (!calls?.length) {
    return false;
  }

  return calls.some(
    call => !/record_id\s*[:=]/.test(call) && !/external_id\s*[:=]/.test(call)
  );
};

const hasRuntimeDomainEntitiesMismatch = (html: string): boolean => {
  const domainMatch = html.match(RUNTIME_DOMAIN_REGEX);
  if (!domainMatch?.[1]) {
    return false;
  }

  let allowed = new Set<string>();
  try {
    const parsed = JSON.parse(domainMatch[1]);
    const entities = Array.isArray(parsed?.entities) ? parsed.entities : [];
    allowed = new Set(
      entities
        .map((entity: { name?: unknown }) =>
          typeof entity?.name === 'string' ? entity.name.trim() : ''
        )
        .filter(Boolean)
    );
  } catch {
    return false;
  }

  if (!allowed.size) {
    return false;
  }

  const usedEntityTypes = new Set<string>();
  const regex = new RegExp(ENTITY_TYPE_REGEX.source, 'g');
  let match: RegExpExecArray | null = regex.exec(html);
  while (match) {
    const entityType = (match[1] ?? '').trim();
    if (entityType) {
      usedEntityTypes.add(entityType);
    }
    match = regex.exec(html);
  }

  return Array.from(usedEntityTypes).some(
    entityType => !allowed.has(entityType)
  );
};

const ANALYSIS_DATASET_REGEX =
  /<script[^>]*id=["']devspott-analysis-dataset["'][^>]*>/i;
const ANALYSIS_DATASET_CONTENT_REGEX =
  /<script[^>]*id=["']devspott-analysis-dataset["'][^>]*>([\s\S]*?)<\/script>/i;

const isReportModeCanvas = (html: string): boolean =>
  ANALYSIS_DATASET_REGEX.test(html);

const hasTruncatedDatasetWithoutCompleteCsv = (html: string): boolean => {
  const match = html.match(ANALYSIS_DATASET_CONTENT_REGEX);
  if (!match?.[1]) {
    return false;
  }

  try {
    const parsed = JSON.parse(match[1]);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return false;
    }

    const dataset = parsed as Record<string, unknown>;
    const rows = Array.isArray(dataset.data) ? dataset.data.length : 0;
    const totalRows = Number(dataset.total_rows);
    const hasNumericTotalRows = Number.isFinite(totalRows);
    const isTruncatedByFlag = dataset.truncated === true;
    const isTruncatedByRows = hasNumericTotalRows && totalRows > rows;
    const requiresFullSpreadsheet = isTruncatedByFlag || isTruncatedByRows;
    if (!requiresFullSpreadsheet) {
      return false;
    }

    const xlsxBase64 =
      typeof dataset.xlsx_base64 === 'string' ? dataset.xlsx_base64.trim() : '';
    const xlsxDownloadUrl =
      typeof dataset.xlsx_download_url === 'string'
        ? dataset.xlsx_download_url.trim()
        : '';
    return !xlsxBase64 && !xlsxDownloadUrl;
  } catch {
    return false;
  }
};

export const validateCanvasDynamicBackendContract = (
  html: string
): CanvasDynamicBackendValidationResult => {
  const missing: string[] = [];
  const reportMode = isReportModeCanvas(html);

  if (!reportMode && !hasContractPrimitive(html, 'data-field=')) {
    missing.push('data-field');
  }
  if (!hasContractPrimitive(html, '__devspottGetState')) {
    missing.push('__devspottGetState');
  }
  if (!hasContractPrimitive(html, '__devspottSetState')) {
    missing.push('__devspottSetState');
  }
  if (!reportMode && !hasContractPrimitive(html, 'runtime_action')) {
    missing.push('runtime_action');
  }
  if (reportMode && hasTruncatedDatasetWithoutCompleteCsv(html)) {
    missing.push('analysis-csv-base64-required');
  }
  if (detectButtonsWithoutAction(html)) {
    missing.push('buttons-without-action');
  }
  if (hasRecordUpdateWithoutIdentity(html)) {
    missing.push('runtime-action-identity');
  }
  if (hasRuntimeDomainEntitiesMismatch(html)) {
    missing.push('runtime-domain-entities-mismatch');
  }

  return {
    isValid: missing.length === 0,
    missing,
  };
};
